import { useCallback, useEffect, useMemo, useRef } from 'react';
import { throttle } from 'lodash';
import { MapView, FeatureLayer, Graphic } from '../constructors';
import { ESRI_BATCHED_HIT_TEST_THROTTLE_THRESHOLD } from '../configurations';
import { getMapIconTheme } from '../helpers/map-icon-theme';
import type { FeatureLayerView, GraphicHit, Layer, ViewPointerMoveEvent } from '../types';

interface Hover {
    layer: Layer;
    objectId: number;
    /** Esri's highlight, or an outline graphic drawn over the feature. */
    handle?: IHandle;
    isOutline: boolean;
}

function isSameFeature(hover: Hover | undefined, graphic: Graphic): boolean {
    return hover?.layer === graphic.layer && hover.objectId === graphic.getObjectId();
}

/**
 * Hook that performs a batched hit test on the map view to handle the hover of map elements.
 * A hovered feature gets Esri's highlight, except an event when the map icon theme has an
 * `eventHoverSymbol`, which is drawn over the event instead.
 *
 * @param {MapView | undefined} mapView - The map view to perform the hit test on.
 * @param {FeatureLayer[]} layers - The layers to include in the hit test.
 * @param {FeatureLayer | undefined} eventsLayer - The layer whose features are events.
 */
export const useBatchedHitTest = (
    mapView: MapView | undefined,
    layers: FeatureLayer[],
    eventsLayer?: FeatureLayer
) => {
    const onDragRef = useRef(false);
    const hoverRef = useRef<Hover>();

    const clearHover = useCallback(function clearHover() {
        hoverRef.current?.handle?.remove();
        hoverRef.current = undefined;
    }, []);

    const pointerMove = useMemo(
        () =>
            throttle(function hoverHitGraphic(event: MouseEvent | ViewPointerMoveEvent) {
                if (onDragRef.current || !mapView) {
                    return;
                }

                mapView
                    .hitTest(event, { include: layers, exclude: mapView.graphics })
                    .then((response) => {
                        const graphic = (response.results as GraphicHit[])[0]?.graphic;

                        if (graphic && isSameFeature(hoverRef.current, graphic)) {
                            return;
                        }
                        clearHover();
                        if (!graphic) {
                            return;
                        }

                        const eventHoverSymbol = getMapIconTheme().eventHoverSymbol;
                        if (eventHoverSymbol && graphic.layer === eventsLayer && graphic.geometry) {
                            const outline = new Graphic({
                                geometry: graphic.geometry,
                                symbol: eventHoverSymbol,
                            });
                            mapView.graphics.add(outline);
                            hoverRef.current = {
                                layer: graphic.layer,
                                objectId: graphic.getObjectId(),
                                handle: { remove: () => mapView.graphics.remove(outline) },
                                isOutline: true,
                            };
                            return;
                        }

                        const hover: Hover = {
                            layer: graphic.layer,
                            objectId: graphic.getObjectId(),
                            isOutline: false,
                        };
                        hoverRef.current = hover;
                        mapView.whenLayerView(graphic.layer).then((layerView) => {
                            const handle = (layerView as FeatureLayerView).highlight(graphic);
                            if (hoverRef.current === hover) {
                                hover.handle = handle;
                            } else {
                                handle.remove();
                            }
                        });
                    })
                    .catch(() => {
                        // This gets rid of the geometry error that we have no control over:
                        // Cannot read properties of undefined (reading 'geometry')
                    });
            }, ESRI_BATCHED_HIT_TEST_THROTTLE_THRESHOLD),
        [clearHover, eventsLayer, layers, mapView]
    );

    useEffect(() => {
        if (!mapView) {
            return;
        }

        const { remove: removeOnDrag } = mapView.on('drag', (event) => {
            if (event.action === 'start') {
                onDragRef.current = true;
            } else if (event.action === 'end') {
                onDragRef.current = false;
            }
        });

        const { remove } = mapView.on('pointer-move', (event) => {
            pointerMove(event);
        });

        return () => {
            remove();
            removeOnDrag();
            pointerMove.cancel();
            clearHover();
        };
    }, [mapView, layers, pointerMove, clearHover]);

    // Unlike Esri's highlight, an outline stays where it was drawn. Drop it when its event moves
    // or goes away; the next pointer move draws it again.
    useEffect(
        function clearOutlineOnEventEdits() {
            if (!eventsLayer) {
                return;
            }
            const { remove } = eventsLayer.on('edits', (event) => {
                const hover = hoverRef.current;
                if (!hover?.isOutline || hover.layer !== eventsLayer) {
                    return;
                }
                const isHoveredEventEdited = [
                    ...event.updatedFeatures,
                    ...event.deletedFeatures,
                ].some(function isHoveredEvent(result) {
                    return result.objectId === hover.objectId;
                });
                if (isHoveredEventEdited) {
                    clearHover();
                }
            });
            return remove;
        },
        [eventsLayer, clearHover]
    );
};
